import { useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";

function operationKey() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 3) | 8).toString(16);
    })
  );
}

type Interest = {
  decision: "unknown" | "interested" | "not_now";
  consentGranted: boolean;
  nextAction: string;
};
function InterestEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Interest;
  onChange: (v: Interest) => void;
}) {
  return (
    <fieldset className="min-w-0 space-y-2 rounded-lg border p-3">
      <legend className="px-1 font-medium">{label}</legend>
      <label className="grid gap-1 text-sm">
        Decisión independiente
        <select
          className="min-h-11 w-full rounded-md border bg-background px-3"
          value={value.decision}
          onChange={e =>
            onChange({
              ...value,
              decision: e.target.value as Interest["decision"],
              ...(e.target.value === "interested"
                ? {}
                : { consentGranted: false }),
            })
          }
        >
          <option value="unknown">Por determinar</option>
          <option value="interested">Interés expresado</option>
          <option value="not_now">No por ahora</option>
        </select>
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.consentGranted}
          disabled={value.decision !== "interested"}
          onChange={e =>
            onChange({ ...value, consentGranted: e.target.checked })
          }
        />
        <span>
          Consentimiento explícito para contacto comercial sobre este tema,
          independiente del contrato EV. No se envía ninguna comunicación desde
          esta pantalla.
        </span>
      </label>
      <label className="grid gap-1 text-sm">
        Próxima acción interna (opcional)
        <input
          className="min-h-10 min-w-0 rounded-md border bg-background px-3"
          maxLength={500}
          value={value.nextAction}
          onChange={e => onChange({ ...value, nextAction: e.target.value })}
        />
      </label>
    </fieldset>
  );
}

export function EvInterestFollowUps({
  originRequestId,
}: {
  originRequestId: number;
}) {
  const utils = trpc.useUtils();
  const rows = trpc.commercialIntake.evInterestFollowUps.useQuery({
    originRequestId,
  });
  const create = trpc.commercialIntake.createEvInterestFollowUp.useMutation();
  const save = trpc.commercialIntake.saveEvInterestFollowUp.useMutation();
  const [drafts, setDrafts] = useState<
    Record<number, { tariff: Interest; photovoltaic: Interest }>
  >({});
  const [message, setMessage] = useState("");
  const currentDraft = (row: any) =>
    drafts[Number(row.id)] ?? {
      tariff: row.need.commercialFollowUp.tariff,
      photovoltaic: row.need.commercialFollowUp.photovoltaic,
    };
  async function createFollowUp() {
    try {
      const result = await create.mutateAsync({
        originRequestId,
        idempotencyKey: operationKey(),
      });
      await utils.commercialIntake.evInterestFollowUps.invalidate({
        originRequestId,
      });
      setMessage(
        `Seguimiento interno #${result.id} creado; sin contacto externo.`
      );
    } catch (error: any) {
      setMessage(error?.message ?? "No se pudo crear el seguimiento.");
    }
  }
  async function saveFollowUp(row: any) {
    const value = currentDraft(row);
    try {
      await save.mutateAsync({
        followUpId: Number(row.id),
        expectedVersion: Number(row.version),
        idempotencyKey: operationKey(),
        ...value,
      });
      await utils.commercialIntake.evInterestFollowUps.invalidate({
        originRequestId,
      });
      setDrafts(current => {
        const next = { ...current };
        delete next[Number(row.id)];
        return next;
      });
      setMessage(`Seguimiento #${row.id} guardado y leído de nuevo.`);
    } catch (error: any) {
      setMessage(error?.message ?? "No se pudo guardar el seguimiento.");
    }
  }
  return (
    <section
      className="min-w-0 space-y-3 rounded-2xl border-l-4 border-primary bg-background p-4"
      aria-labelledby={`ev-follow-up-${originRequestId}`}
    >
      <div>
        <h3 id={`ev-follow-up-${originRequestId}`} className="font-semibold">
          Seguimiento independiente · tarifa y fotovoltaica
        </h3>
        <p className="text-sm text-muted-foreground">
          Registro separado de la solicitud EV formalizada. No modifica su
          oferta, contrato ni consentimiento y no envía comunicaciones.
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => void createFollowUp()}
        disabled={create.isPending}
      >
        Crear seguimiento interno
      </Button>
      {rows.isLoading && (
        <p role="status" className="text-sm">
          Cargando seguimientos…
        </p>
      )}
      {(rows.data ?? []).map((row: any) => (
        <div key={row.id} className="space-y-3 rounded-xl border p-3">
          <div className="flex flex-wrap justify-between gap-2 text-sm">
            <b>
              Seguimiento #{row.id} · origen EV #
              {row.need.commercialFollowUp.origin.requestId} · acuerdo #
              {row.need.commercialFollowUp.origin.agreementId}
            </b>
            <span>v{row.version}</span>
          </div>
          {(["tariff", "photovoltaic"] as const).map(kind => (
            <InterestEditor
              key={kind}
              label={kind === "tariff" ? "Tarifa" : "Fotovoltaica"}
              value={currentDraft(row)[kind]}
              onChange={value =>
                setDrafts(current => ({
                  ...current,
                  [Number(row.id)]: { ...currentDraft(row), [kind]: value },
                }))
              }
            />
          ))}
          <Button
            size="sm"
            onClick={() => void saveFollowUp(row)}
            disabled={save.isPending}
          >
            Guardar decisiones del seguimiento
          </Button>
        </div>
      ))}
      {message && (
        <p role="status" className="text-sm">
          {message}
        </p>
      )}
      {create.error && (
        <p role="alert" className="text-sm text-destructive">
          {create.error.message}
        </p>
      )}
      {save.error && (
        <p role="alert" className="text-sm text-destructive">
          {save.error.message}
        </p>
      )}
    </section>
  );
}
